import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

interface OpportunityData {
  id: string
  pipeline_id: string
  stage_id: string
  pipeline_name?: string
  stage_name?: string
}

export interface WonOpportunityData {
  product?: string
  price?: number
  sale_date?: string
  won_by_user_id?: string
  notes?: string
}

interface UseContactOpportunityReturn {
  creating: boolean
  updating: boolean
  deleting: boolean
  closing: boolean
  error: string | null
  createOrUpdateOpportunity: (contactId: string, pipelineId: string, stageId: string) => Promise<boolean>
  getContactOpportunity: (contactId: string) => Promise<OpportunityData | null>
  deleteOpportunity: (opportunityId: string) => Promise<boolean>
  closeOpportunity: (
    contactId: string,
    pipelineId: string,
    stageId: string,
    status: 'won' | 'lost',
    lossReasonId?: string,
    wonData?: WonOpportunityData
  ) => Promise<boolean>
}

export const useContactOpportunity = (): UseContactOpportunityReturn => {
  const { user } = useAuth()
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [closing, setClosing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Busca a oportunidade atual do contato com nomes de pipeline e stage
   */
  const getContactOpportunity = async (contactId: string): Promise<OpportunityData | null> => {
    if (!user?.account_id || !supabase) return null

    try {
      const { data, error: fetchError } = await supabase
        .from('account_opportunities')
        .select(`
          id,
          pipeline_id,
          stage_id,
          account_pipelines!inner(name),
          account_pipeline_stages!inner(name)
        `)
        .eq('contact_id', contactId)
        .eq('account_id', user.account_id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (fetchError) throw fetchError

      if (!data) return null

      return {
        id: data.id,
        pipeline_id: data.pipeline_id,
        stage_id: data.stage_id,
        pipeline_name: (data.account_pipelines as any)?.name,
        stage_name: (data.account_pipeline_stages as any)?.name,
      }
    } catch (err: any) {
      console.error('💥 [useContactOpportunity] Erro ao buscar oportunidade:', err)
      return null
    }
  }

  /**
   * Cria ou atualiza a oportunidade do contato em um funil/etapa
   */
  const createOrUpdateOpportunity = async (
    contactId: string,
    pipelineId: string,
    stageId: string
  ): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setError(null)

      // Verificar se já existe uma oportunidade para este contato no mesmo funil
      const { data: existingOpp, error: fetchError } = await supabase
        .from('account_opportunities')
        .select('id, pipeline_id, stage_id')
        .eq('contact_id', contactId)
        .eq('account_id', user.account_id)
        .eq('pipeline_id', pipelineId)
        .maybeSingle()

      if (fetchError) throw fetchError

      if (existingOpp) {
        // Atualizar oportunidade existente
        setUpdating(true)

        const { error: updateError } = await supabase
          .from('account_opportunities')
          .update({
            pipeline_id: pipelineId,
            stage_id: stageId,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingOpp.id)
          .eq('account_id', user.account_id)

        if (updateError) throw updateError

        console.log('✅ [useContactOpportunity] Oportunidade atualizada:', {
          opportunityId: existingOpp.id,
          contactId,
          pipelineId,
          stageId
        })
      } else {
        // Criar nova oportunidade
        setCreating(true)

        // Buscar informações do contato para criar título
        const { data: contact, error: contactError } = await supabase
          .from('account_contacts')
          .select('name, jid')
          .eq('id', contactId)
          .eq('account_id', user.account_id)
          .maybeSingle()

        if (contactError) throw contactError

        const contactName = contact?.name || contact?.jid?.split('@')[0] || 'Contato'
        const title = `Oportunidade - ${contactName}`

        const { data: newOpp, error: insertError } = await supabase
          .from('account_opportunities')
          .insert({
            account_id: user.account_id,
            contact_id: contactId,
            pipeline_id: pipelineId,
            stage_id: stageId,
            title: title,
            price: 0
          })
          .select('id')
          .single()

        if (insertError) throw insertError

        console.log('✅ [useContactOpportunity] Oportunidade criada:', {
          opportunityId: newOpp.id,
          contactId,
          pipelineId,
          stageId,
          title
        })
      }

      return true
    } catch (err: any) {
      console.error('💥 [useContactOpportunity] Erro ao criar/atualizar oportunidade:', err)
      setError(err.message || 'Erro ao processar oportunidade')
      return false
    } finally {
      setCreating(false)
      setUpdating(false)
    }
  }

  /**
   * Remove uma oportunidade (delete permanente)
   */
  const deleteOpportunity = async (opportunityId: string): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setDeleting(true)
      setError(null)

      const { error: deleteError } = await supabase
        .from('account_opportunities')
        .delete()
        .eq('id', opportunityId)
        .eq('account_id', user.account_id)

      if (deleteError) throw deleteError

      console.log('✅ [useContactOpportunity] Oportunidade removida:', opportunityId)
      return true
    } catch (err: any) {
      console.error('💥 [useContactOpportunity] Erro ao remover oportunidade:', err)
      setError(err.message || 'Erro ao remover oportunidade')
      return false
    } finally {
      setDeleting(false)
    }
  }

  const closeOpportunity = async (
    contactId: string,
    pipelineId: string,
    stageId: string,
    status: 'won' | 'lost',
    lossReasonId?: string,
    wonData?: WonOpportunityData
  ): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setClosing(true)
      setError(null)

      const { data: existingOpp, error: fetchError } = await supabase
        .from('account_opportunities')
        .select('id')
        .eq('contact_id', contactId)
        .eq('account_id', user.account_id)
        .eq('pipeline_id', pipelineId)
        .maybeSingle()

      if (fetchError) throw fetchError

      const updateData: Record<string, unknown> = {
        stage_id: stageId,
        status: status,
        updated_at: new Date().toISOString(),
        closed_at: new Date().toISOString(),
      }

      if (status === 'lost' && lossReasonId) {
        updateData.loss_reason_id = lossReasonId
      }

      if (status === 'won' && wonData) {
        updateData.product = wonData.product
        updateData.price = wonData.price
        updateData.sale_date = wonData.sale_date
        updateData.won_by_user_id = wonData.won_by_user_id
        updateData.notes = wonData.notes
      }

      if (existingOpp) {
        const { error: updateError } = await supabase
          .from('account_opportunities')
          .update(updateData)
          .eq('id', existingOpp.id)
          .eq('account_id', user.account_id)

        if (updateError) throw updateError
      } else {
        const { data: contact } = await supabase
          .from('account_contacts')
          .select('name, jid')
          .eq('id', contactId)
          .eq('account_id', user.account_id)
          .maybeSingle()

        const contactName = contact?.name || contact?.jid?.split('@')[0] || 'Contato'
        const title = `Oportunidade - ${contactName}`

        const { error: insertError } = await supabase
          .from('account_opportunities')
          .insert({
            account_id: user.account_id,
            contact_id: contactId,
            pipeline_id: pipelineId,
            title: title,
            ...updateData
          })

        if (insertError) throw insertError
      }

      return true
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao fechar oportunidade'
      console.error('[useContactOpportunity] Erro ao fechar oportunidade:', err)
      setError(errorMessage)
      return false
    } finally {
      setClosing(false)
    }
  }

  return {
    creating,
    updating,
    deleting,
    closing,
    error,
    createOrUpdateOpportunity,
    getContactOpportunity,
    deleteOpportunity,
    closeOpportunity,
  }
}
